import React, { useEffect, useState } from "react";
import { API_URL } from "../config";
import { Button } from "@mui/material";
import {
  Box,
  Stack,
  Typography,
  TextField,

  CircularProgress,
} from "@mui/material";
import {
  FaPlus,
  FaCheck,
  FaCalendarAlt,
  FaToggleOn,
  FaToggleOff,
} from "react-icons/fa";
import { MdOutlineEventNote } from "react-icons/md";
import TermModal from "../components/modal/termModal";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";

/* ─── Design Tokens ─────────────────────────────────────────── */
const T = {
  indigo: "#3949AB",
  indigoLight: "#5C6BC0",
  indigoGhost: "rgba(57,73,171,0.08)",
  indigoGhostHover: "rgba(57,73,171,0.14)",
  teal: "#00796B",
  tealGhost: "rgba(0,121,107,0.1)",
  slate900: "#0D1B2A",
  slate700: "#334155",
  slate500: "#64748B",
  slate400: "#94A3B8",
  slate200: "#E2E8F0",
  slate100: "#F1F5F9",
  slate50: "#F8FAFC",
  white: "#FFFFFF",
  amber: "#F57C00",
  rose: "#C62828",
  shadow: "0 1px 3px rgba(13,27,42,0.06), 0 4px 12px rgba(13,27,42,0.04)",
};

/* ─── Status Badge ───────────────────────────────────────────── */
const StatusBadge = ({ isDone, onClick }) => (
  <Box
    onClick={onClick}
    component={motion.div}
    whileHover={{ scale: 1.04 }}
    whileTap={{ scale: 0.97 }}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.6,
      px: 1.2,
      py: 0.35,
      borderRadius: 99,
      cursor: "pointer",
      background: isDone ? T.slate100 : T.tealGhost,
      border: `1px solid ${isDone ? T.slate200 : "rgba(0,121,107,0.25)"}`,
      transition: "all 0.2s ease",
      "&:hover": {
        boxShadow: `0 2px 8px ${
          isDone ? "rgba(100,116,139,0.15)" : "rgba(0,121,107,0.2)"
        }`,
      },
    }}
  >
    {isDone ? (
      <FaToggleOff size={12} color={T.slate400} />
    ) : (
      <FaToggleOn size={12} color={T.teal} />
    )}
    <Typography
      sx={{
        fontSize: "0.62rem",
        fontWeight: 800,
        color: isDone ? T.slate400 : T.teal,
        textTransform: "uppercase",
        letterSpacing: "0.6px",
      }}
    >
      {isDone ? "Inactive" : "Active"}
    </Typography>
  </Box>
);

/* ─── Term Item ──────────────────────────────────────────────── */
const TermItem = ({ data, onToggle, index }) => (
  <motion.div
    key={data.id}
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{
      duration: 0.28,
      delay: index * 0.05,
      ease: [0.22, 1, 0.36, 1],
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        py: 1.4,
        borderRadius: 2,
        background: T.white,
        border: `1px solid ${T.slate200}`,
        boxShadow: T.shadow,
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: `${T.indigo}35`,
          boxShadow: `0 3px 12px rgba(57,73,171,0.08)`,
          transform: "translateY(-1px)",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.8,
            flexShrink: 0,
            background: data.is_done
              ? `${T.slate400}15`
              : "linear-gradient(135deg, rgba(57,73,171,0.12), rgba(92,107,192,0.18))",
            border: `1px solid ${
              data.is_done ? T.slate200 : "rgba(57,73,171,0.22)"
            }`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.25s ease",
          }}
        >
          <MdOutlineEventNote
            size={14}
            color={data.is_done ? T.slate400 : T.indigo}
          />
        </Box>
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              color: data.is_done ? T.slate500 : T.slate900,
              fontSize: "0.84rem",
              textTransform: "capitalize",
              textDecoration: data.is_done ? "line-through" : "none",
              textDecorationColor: T.slate300,
            }}
          >
            {data.term}
          </Typography>
          <Typography
            sx={{ fontSize: "0.62rem", color: T.slate400, fontWeight: 500 }}
          >
            {data.is_done ? "Term completed" : "Currently running"}
          </Typography>
        </Box>
      </Stack>
      <StatusBadge isDone={data.is_done} onClick={() => onToggle(data)} />
    </Box>
  </motion.div>
);

/* ─── Main Component ─────────────────────────────────────────── */
const TermSemesterForm = ({ token, allTerms, refreshTerms }) => {
  const [term, setTerm] = useState("");
  const [currentTerm, setCurrentTerm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    const fetchTerm = async () => {
      try {
        const res = await fetch(`${API_URL}/term-semester/`, {
          headers: { Authorization: `Bearer ${token.access}` },
        });
        if (res.ok) {
          const text = await res.text();
          if (text) {
            const data = JSON.parse(text);
            setCurrentTerm(data);
            setTerm(data.term || "");
          } else {
            setCurrentTerm(null);
            setTerm("");
          }
        }
      } catch (err) {
        console.error("Error fetching term:", err);
      }
    };
    fetchTerm();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!term.trim()) return;
    setLoading(true);
    const method = currentTerm ? "PUT" : "POST";
    const url = currentTerm
      ? `${API_URL}/term-semester/${currentTerm.id}/`
      : `${API_URL}/term-semester/`;
    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.access}`,
        },
        body: JSON.stringify({ term }),
      });
      const data = await res.json();
      if (res.ok) {
        setCurrentTerm(data);
        setTerm(data.term);
        await refreshTerms();
        toast.success("Term saved successfully.");
      } else {
        toast.error("Error saving term: " + JSON.stringify(data));
      }
    } catch (err) {
      console.error("Submit error:", err);
    } finally {
      setLoading(false);
      setTerm("");
    }
  };

  const handleActivateTerm = async () => {
    if (!selectedTerm) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/term-semester/${selectedTerm.id}/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.access}`,
        },
        body: JSON.stringify({
          term: selectedTerm.term,
          is_done: !selectedTerm.is_done,
        }),
      });
      if (res.ok) {
        await refreshTerms();
        setOpenModal(false);
      } else {
        const err = await res.json();
        toast.error("Error: " + JSON.stringify(err));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleClick = (data) => {
    setSelectedTerm(data);
    setOpenModal(true);
  };

  return (
    <Box>
      {/* ── Add / Edit Form ──────────────────────────────────── */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        sx={{
          p: 2.2,
          mb: 2.5,
          borderRadius: 2.5,
          background:
            "linear-gradient(135deg, rgba(57,73,171,0.05) 0%, rgba(92,107,192,0.03) 100%)",
          border: `1px solid rgba(57,73,171,0.14)`,
        }}
      >
        {/* Label */}
        <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: 1,
              background: currentTerm
                ? "linear-gradient(135deg, #F57C00, #FFA726)"
                : "linear-gradient(135deg, #3949AB, #5C6BC0)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {currentTerm ? (
              <FaCheck size={9} color="#fff" />
            ) : (
              <FaPlus size={9} color="#fff" />
            )}
          </Box>
          <Typography
            sx={{
              fontSize: "0.65rem",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.9px",
              color: currentTerm ? T.amber : T.indigo,
            }}
          >
            {currentTerm ? "Update Current Term" : "Create New Term"}
          </Typography>
        </Stack>

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={1.4}>
            <TextField
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="e.g. First Term, Semester 1, Q1 2024"
              required
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
                    <MdOutlineEventNote size={15} color={T.indigoLight} />
                  </Box>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  background: T.white,
                  color: T.slate900,
                  fontSize: "0.85rem",
                  "& fieldset": { borderColor: "rgba(57,73,171,0.2)" },
                  "&:hover fieldset": { borderColor: T.indigo },
                  "&.Mui-focused fieldset": {
                    borderColor: T.indigo,
                    borderWidth: 1.5,
                  },
                },
                "& input::placeholder": {
                  color: T.slate400,
                  fontSize: "0.82rem",
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
              startIcon={
                loading ? null : currentTerm ? (
                  <FaCheck size={11} />
                ) : (
                  <FaPlus size={11} />
                )
              }
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.83rem",
                py: 0.9,
                background: currentTerm
                  ? "linear-gradient(135deg, #F57C00, #FFA726)"
                  : "linear-gradient(135deg, #3949AB, #5C6BC0)",
                boxShadow: currentTerm
                  ? "0 4px 12px rgba(245,124,0,0.3)"
                  : "0 4px 12px rgba(57,73,171,0.28)",
                "&:hover": {
                  background: currentTerm
                    ? "linear-gradient(135deg, #E65100, #F57C00)"
                    : "linear-gradient(135deg, #283593, #3949AB)",
                },
                "&:disabled": { opacity: 0.5 },
              }}
            >
              {loading ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : currentTerm ? (
                "Update Term"
              ) : (
                "Create Term"
              )}
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* ── Terms List ───────────────────────────────────────── */}
      <AnimatePresence>
        {allTerms?.length > 0 ? (
          <Stack spacing={1}>
            {/* Section label */}
            <Typography
              sx={{
                fontSize: "0.62rem",
                fontWeight: 800,
                color: T.slate400,
                textTransform: "uppercase",
                letterSpacing: "0.9px",
                mb: 0.5,
              }}
            >
              Academic Terms ({allTerms.length})
            </Typography>
            {allTerms.map((data, index) => (
              <TermItem
                key={data.id}
                data={data}
                index={index}
                onToggle={handleToggleClick}
              />
            ))}
          </Stack>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                px: 2,
                border: `1.5px dashed rgba(57,73,171,0.18)`,
                borderRadius: 2.5,
                background: T.indigoGhost,
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  background: "rgba(57,73,171,0.1)",
                  border: `1px dashed rgba(57,73,171,0.25)`,
                  mx: "auto",
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaCalendarAlt size={18} color={T.slate400} />
              </Box>
              <Typography
                sx={{
                  fontSize: "0.82rem",
                  fontWeight: 700,
                  color: T.slate500,
                  mb: 0.5,
                }}
              >
                No terms yet
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: T.slate400 }}>
                Create your first academic term above.
              </Typography>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      <TermModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        handleActivateTerm={handleActivateTerm}
        selectedTerm={selectedTerm}
        actionLoading={actionLoading}
      />
    </Box>
  );
};

export default TermSemesterForm;
